'use client';

import { TeaserSocial as TeaserSocialType } from '@/shared/types/teasers';
import HIHButton from '../base/HIHButton';
import Heart from '../base/Heart';
import HIHImage from '../base/HIHImage';
import TripleLines from '../base/TripleLines';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Scrollbar } from 'swiper/modules';
import { ImageMeta } from '@/shared/types/imageMeta';
import { ReactNode } from 'react';
import HIHLink from '../base/Link';
import styles from './TeaserSocial.module.scss';

export default function TeaserSocial({
  teaser,
  className,
}: {
  teaser: TeaserSocialType;
  className?: string;
}) {
  const { title, copy, postings } = teaser;

  const getSinglePosting = (
    posting: { _key: string; link: string; image: ImageMeta },
    index: number,
    breakpoint: 'sm' | 'md' | 'lg',
  ) => {
    const isSpecial = index === 0 || index === postings.length - 1;
    const width = breakpoint == 'lg' ? 280 : breakpoint == 'md' ? 198 : 132;
    const height = breakpoint == 'lg' ? 331 : breakpoint == 'md' ? 234 : 156;

    const image = (
      <HIHImage
        image={posting.image}
        width={width}
        height={height}
        className={styles.postingImage}
      />
    );
    const content = posting.link ? (
      <HIHLink key={posting._key || index} href={posting.link} target="_blank">
        {image}
      </HIHLink>
    ) : (
      image
    );

    return (
      <div key={index}>
        {isSpecial ? (
          <TripleLines
            key={posting._key || index}
            variant={index === 0 ? 'topLeft' : 'bottomRight'}
            isLargeSize
            isContentFullWidth={true}
          >
            <div
              className={
                breakpoint === 'lg'
                  ? index === 0
                    ? styles.rotateLeft
                    : styles.rotateRight
                  : undefined
              }
            >
              {content}
            </div>
          </TripleLines>
        ) : (
          content
        )}
      </div>
    );
  };

  function postingList(breakpoint: 'sm' | 'md' | 'lg'): ReactNode {
    if (breakpoint !== 'lg') {
      return (
        <div className={styles.mobileSliderWrapper}>
          <Swiper
            modules={[Scrollbar]}
            spaceBetween={8}
            slidesPerView={'auto'}
            className={styles.swiperFull}
            slidesOffsetBefore={24}
            slidesOffsetAfter={24}
          >
            {postings.map((posting, index) => (
              <SwiperSlide key={posting._key || index} className={styles.swiperSlide}>
                {getSinglePosting(posting, index, breakpoint)}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      );
    } else {
      return (
        <div className={styles.desktopPostingsList}>
          {postings.map((posting, index) => getSinglePosting(posting, index, breakpoint))}
        </div>
      );
    }
  }

  return (
    <div className={className}>
      <div className={styles.wrapper}>
        <div className={styles.titleRow}>
          <div className={styles.title}>{title}</div>
          <Heart amount={2} />
        </div>

        <div className={styles.mobileSmSlider}>{postingList('sm')}</div>
        <div className={styles.mobileMdSlider}>{postingList('md')}</div>
        <div className={styles.desktopPostings}>{postingList('lg')}</div>

        <p className={styles.copy}>{copy}</p>
        <HIHButton
          link="https://www.instagram.com/heuteinhamburg.byhaspa/"
          title="Folge uns auf Instagram"
          blank
        />
      </div>
    </div>
  );
}
